import {call, put, takeEvery, takeLatest, select} from "redux-saga/effects";

import {fetchAdminOrders} from "../../api/order-requests";

import {
    actionCustomerReviewUpdate,
    actionCustomersUpdate,
    actionCustomerUpdate,
    CUSTOMER_ADD_BONUS_CARD, CUSTOMER_BLOCK_USER, CUSTOMER_BLOCK_USER_STATUS, CUSTOMER_DELETE_VOUCHER,
    CUSTOMER_ORDERS_GET,
    CUSTOMER_REVIEWS_GET, CUSTOMER_REVIEWS_GET_REPORT, CUSTOMER_UPDATE_COMMENT, CUSTOMER_UPDATE_LOYALTY_LEVEL,
    CUSTOMER_WALLET_INFO_GET,
    CUSTOMER_WALLET_OPERATION,
    CUSTOMERS_GET,
    CUSTOMERS_GET_REPORT,
} from '../reducers/customer/customer-actions';

import {objectKeysToUpperLowerCase} from "../../utils/objects-util";
import {getErrorMessage, requestResultIsOk} from "../../utils/request-util";
import {createSaga, ErrorData} from "../../utils/sagaHelper";
import {
    blockConsumerUser,
    customerAddBonusCard, customerDeleteonusCard,
    customerWalletInfo,
    customerWalletOperation, getblockConsumerUserStatus,
    getCustomers, unblockConsumerUser, updateCommentConsumerUser, updateLoyaltyLevel,
} from '../../api/customer-requests';
import {CUSTOMER_WALLET_OPERATION_TYPE} from "../../utils/constants";
import { addHistoryReport } from "../../api/reportsHistory-request";
import { ReportAction } from "../../utils/enums";
import { actionUpdateCustomTableOrder } from "../reducers/order/order-actions";

export function* customerReviewGetSaga(action) {
    yield* createSaga(function*() {
        const {periodFilter, ...filter} = action.data || {};
        const requestResult = yield call(fetchAdminOrders, objectKeysToUpperLowerCase({
            ...(filter || {}),
            end: new Date(action.data.end).toISOString(),
            start: new Date(action.data.start).toISOString(),
            withRating: true
        }, true));

        if(requestResultIsOk(requestResult)) {
            yield put(actionCustomerReviewUpdate(objectKeysToUpperLowerCase(requestResult.data)));
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

export function* customersGetSaga(action) {
    yield* createSaga(function*() {
        const data = action.data || {};
        const requestResult = yield call(getCustomers, data);
        if(requestResultIsOk(requestResult)) {
            const response = objectKeysToUpperLowerCase(requestResult.data);
            yield put(actionCustomersUpdate(response));
            return response;
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

export function* customerOrdersGetSaga(action) {
    yield* createSaga(function*() {
        const { customerId, rating, deliveryRating } = action.data || {};
        if(customerId) {
            const requestResult = yield call(fetchAdminOrders, objectKeysToUpperLowerCase({
                search: customerId,
                rating, deliveryRating,
                withRating: !!(rating || deliveryRating)
            }, true));

            if(requestResultIsOk(requestResult)) {
                yield put(actionCustomerUpdate({ consumerId: customerId, orders: objectKeysToUpperLowerCase(requestResult.data) }));
            } else {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerWalletOperationSaga(action) {
    yield* createSaga(function*() {
        const { customerId, type, data } = action.data;
        if(customerId && type && data) {
            const requestResult = yield call(customerWalletOperation, customerId, {
                ...data,
                is_deduct: type === CUSTOMER_WALLET_OPERATION_TYPE.spend
            });

            if(!requestResultIsOk(requestResult)) {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerAddBonusCardSaga(action) {
    yield* createSaga(function*() {
        const { customerId, data } = action.data;
        if(customerId && data) {
            const requestResult = yield call(customerAddBonusCard, customerId, data);

            const users = yield select(state => state?.order?.data?.customTableConfigPagination?.customerServiceCustomers?.orders?.Page);
            const user = users?.find(u => u.ConsumerId === customerId);
            if(user) {
                yield put(actionUpdateCustomTableOrder({
                    key: 'customerServiceCustomers', mapKey: 'ConsumerId', data: {
                        ConsumerId: customerId,
                        wallet: {
                            ...user.wallet,
                            bonusCards: objectKeysToUpperLowerCase(requestResult.data),
                        }
                    },
                }));
            }

            if(!requestResultIsOk(requestResult)) {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerUpdateLoyaltyLevelSaga(action) {
    yield* createSaga(function*() {
        const { customerId, loyaltyLevel } = action.data;
        if(customerId) {
            const requestResult = yield call(updateLoyaltyLevel, customerId, loyaltyLevel);
            yield put(actionUpdateCustomTableOrder({
                key: 'customerServiceCustomers', mapKey: 'ConsumerId', data: requestResult.data,
            }));

            if(!requestResultIsOk(requestResult)) {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerBlockUserSaga(action) {
    yield* createSaga(function*() {
        const { customerId, notes, isBlocked } = action.data;
        if(customerId) {
            const requestResult = yield call(isBlocked ? unblockConsumerUser : blockConsumerUser, customerId, notes);

            yield put(actionUpdateCustomTableOrder({
                key: 'customerServiceCustomers', mapKey: 'ConsumerId', data: {
                    ConsumerId: customerId, blockInfo: !isBlocked ? { note: notes, isBlocked: true } :{ isBlocked: false, note: '' }
                },
            }));

            if(!requestResultIsOk(requestResult)) {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerUpdateCommentSaga(action) {
    yield* createSaga(function*() {
        const { customerId, notes } = action.data;
        if(customerId) {
            const requestResult = yield call(updateCommentConsumerUser, customerId, notes);
            if(requestResultIsOk(requestResult)) {
                const response = requestResult.data;
                delete response.wallet;

                yield put(actionUpdateCustomTableOrder({
                    key: 'customerServiceCustomers', mapKey: 'ConsumerId', data: response,
                }));
            }

            if(!requestResultIsOk(requestResult)) {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerDeleteVoucherSaga(action) {
    yield* createSaga(function*() {
        const {cardId, customerId} = action.data;
        const requestResult = yield call(customerDeleteonusCard, cardId);

        if(requestResultIsOk(requestResult)) {
            const users = yield select(state => state?.order?.data?.customTableConfigPagination?.customerServiceCustomers?.orders?.Page);
            const user = users?.find(u => u.ConsumerId === customerId);
            if(user) {
                yield put(actionUpdateCustomTableOrder({
                    key: 'customerServiceCustomers', mapKey: 'ConsumerId', data: {
                        ConsumerId: customerId,
                        wallet: {
                            ...user.wallet,
                            bonusCards: user.wallet.bonusCards.filter(voucher => voucher.id !== cardId)
                        }
                    },
                }));
            }
            return;
        }

        if(!requestResultIsOk(requestResult)) {
            return new ErrorData(getErrorMessage(requestResult));
        }
    }, action.type);
}

export function* customerWalletInfoGetSaga(action) {
    yield* createSaga(function*() {
        const { customerId } = action.data;

        if(customerId) {
            const requestResult = yield call(customerWalletInfo, customerId);
            if(requestResultIsOk(requestResult)) {
                const response = objectKeysToUpperLowerCase(requestResult.data);
                yield put(actionUpdateCustomTableOrder({
                    key: 'customerServiceCustomers', mapKey: 'ConsumerId', data: {ConsumerId: customerId, wallet: response},
                }));
                return response;
            } else {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerBlockInfoGetSaga(action) {
    yield* createSaga(function*() {
        const { customerId } = action.data;

        if(customerId) {
            const requestResult = yield call(getblockConsumerUserStatus, customerId);
            if(requestResultIsOk(requestResult)) {
                const data = objectKeysToUpperLowerCase(requestResult.data);
                yield put(actionUpdateCustomTableOrder({
                    key: 'customerServiceCustomers', mapKey: 'ConsumerId', data: {ConsumerId: customerId, blockInfo: data ? { note: data.notes, isBlocked: true } : { isBlocked: false, note: '' }},
                }));
            } else {
                return new ErrorData(getErrorMessage(requestResult));
            }
        }
    }, action.type);
}

export function* customerReviewGetReportSaga(action) {
     yield* createSaga(function*() {
        const tableData = yield select(state => state.order.data?.customTableConfigFilters?.customerServiceReview || {});

        const requestResult = yield call(
            addHistoryReport,
            {
                ReportId: action.data.reportAction === ReportAction.Regenerate ? action.data.reportId : undefined,
                ReportType: action.data.reportType,
                Action: action.data.reportAction ?? 0,
                Start: tableData?.creationDateTime?.value ?? new Date(),
                End: tableData?.creationDateTime?.value2 ?? new Date(),
                WithRating: true,
                // Rating: tableData?.rating?.value?.map(c => c.key) ?? 0,
                // DeliveryRating: tableData?.deliveryRating?.value?.map(c => c.key) ?? 0,
            },
        );

        if (requestResult && [200, 201, 202].includes(requestResult.status)) {
            return objectKeysToUpperLowerCase(requestResult.data);
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }


    }, action.type);
}

export function* customerGetReportSaga(action) {
     yield* createSaga(function*() {
        const requestResult = yield call(
            addHistoryReport,
            {
                ReportId: action.data.reportAction === ReportAction.Regenerate ? action.data.reportId : undefined,
                ReportType: action.data.reportType,
                Action: action.data.reportAction ?? 0,
                Search: action.data.model.search,
                ReportFormat: action.data.reportFormat,
                Start: (new Date(action.data.model.startDate)).toISOString(),
                End: (new Date(action.data.model.endDate)).toISOString(),
            },
        );

        if (requestResult && [200, 201, 202].includes(requestResult.status)) {
            return objectKeysToUpperLowerCase(requestResult.data);
        } else {
            return new ErrorData(getErrorMessage(requestResult));
        }


    }, action.type);
}

export default function*() {
    yield takeEvery(CUSTOMER_REVIEWS_GET, customerReviewGetSaga);
    yield takeEvery(CUSTOMER_REVIEWS_GET_REPORT, customerReviewGetReportSaga);
    yield takeEvery(CUSTOMER_ORDERS_GET, customerOrdersGetSaga);
    yield takeLatest(CUSTOMERS_GET, customersGetSaga);
    yield takeLatest(CUSTOMERS_GET_REPORT, customerGetReportSaga);
    yield takeEvery(CUSTOMER_WALLET_OPERATION, customerWalletOperationSaga);
    yield takeEvery(CUSTOMER_ADD_BONUS_CARD, customerAddBonusCardSaga);
    yield takeEvery(CUSTOMER_UPDATE_LOYALTY_LEVEL, customerUpdateLoyaltyLevelSaga);
    yield takeEvery(CUSTOMER_BLOCK_USER, customerBlockUserSaga);
    yield takeEvery(CUSTOMER_UPDATE_COMMENT, customerUpdateCommentSaga);
    yield takeEvery(CUSTOMER_DELETE_VOUCHER, customerDeleteVoucherSaga);
    yield takeLatest(CUSTOMER_WALLET_INFO_GET, customerWalletInfoGetSaga);
    yield takeEvery(CUSTOMER_BLOCK_USER_STATUS, customerBlockInfoGetSaga);
}
